/**
 * Onboarding hero photos (web, #3439).
 *
 * The three Unsplash heroes web renders on Welcome / Notifications / Completion
 * steps. Each entry co-locates the served `/images/onboarding/` path with its
 * Unsplash attribution facts (photographer + profile handle feed
 * `PhotoAttribution` for license compliance, #765) AND a `focalPoint` so the
 * cover-fit crop into the tall portrait media region is CHOSEN, not centred by
 * default — the crop that lost the subjects in #3439.
 *
 * This mirrors native's `mobile/src/components/onboarding/heroImages.ts`: same
 * per-step shape, same focal-point numbers, so the two surfaces render the same
 * crop. The image and its focal point travel together here; the steps read it
 * (via `heroObjectPosition`) rather than hard-coding a position or a `src`.
 *
 * All three crops are now true portrait frames of the SAME photographs (owner
 * decision 2026-09-11: keep the photos, fix the crop). Notifications and
 * completion were re-cropped from their portrait Unsplash originals (they had
 * been landscape 16:9 slices); welcome was already portrait. Notifications and
 * completion record a `photoId` / `photoUrl` so the frame never has to be
 * re-derived from a handle alone; welcome's original was not recoverable under
 * the recorded handle, so it records neither (see #3439). The filenames are
 * kept — the `hero-vertical-` stem is now true for all three.
 */

/** A focal point as a percentage of the frame (0 = left/top, 100 = right/bottom). */
export interface HeroFocalPoint {
  readonly x: number;
  readonly y: number;
}

export const ONBOARDING_HERO = {
  welcome: {
    src: '/images/onboarding/hero-vertical-bridge-runner-fabiocomparelli.jpg',
    alt: 'Runner on a bridge at sunrise',
    photographer: 'Fabio Comparelli',
    unsplashUsername: 'fabioha',
    // Runner is centred horizontally, low-middle vertically; bias down to keep
    // the runner + bridge and trim the bright sky.
    focalPoint: { x: 50, y: 58 },
  },
  notifications: {
    src: '/images/onboarding/hero-vertical-mtb-forest-group-jakubcihak_cp.jpg',
    alt: 'Mountain bikers riding together through a forest',
    photographer: 'Jakub Čihák',
    unsplashUsername: 'kubacihak',
    photoId: '2ckDG8MYPcQ',
    photoUrl: 'https://unsplash.com/photos/2ckDG8MYPcQ',
    // Portrait 2:3 frame; the front two riders sit centred, low-middle, with the
    // pack and the canopy tunnel behind. 50/62 keeps the lead riders through a
    // taller cover-fit while retaining the rooty trail foreground.
    focalPoint: { x: 50, y: 62 },
  },
  completion: {
    src: '/images/onboarding/hero-vertical-forest-runner-action-gregrosenke_cp.jpg',
    alt: 'Runner in motion through a forest trail',
    photographer: 'Greg Rosenke',
    unsplashUsername: 'greg_rosenke',
    photoId: 'E9PdTVfEujI',
    photoUrl: 'https://unsplash.com/photos/E9PdTVfEujI',
    // Portrait 4:5 frame; the runner is right of centre in the lower third,
    // mid-stride. 55/62 keeps the whole figure and the mossy bank through a
    // taller cover-fit.
    focalPoint: { x: 55, y: 62 },
  },
} as const;

/**
 * Convert a {@link HeroFocalPoint} to a CSS `object-position` value. Native's
 * expo-image `contentPosition` object form anchors the same `left`/`top`
 * percentage, so both surfaces read the SAME focal-point numbers.
 */
export function heroObjectPosition(fp: HeroFocalPoint): `${number}% ${number}%` {
  return `${fp.x}% ${fp.y}%`;
}
